import { Product, parseProduct, BatteryLevelEnum, MagneticLevelEnum, SensitivityLevelEnum } from '../../product-library/utils/productParser';
import { fetchDemoProducts, fetchUserProducts } from '../../product-library/utils/productApi';
import { createContext, useState, useEffect, useCallback, useContext } from 'react';

interface ProductsContextType {
    products: Product[];
    isLoading: boolean;
    refreshProducts: () => Promise<void>;
    addProduct: (product: Product) => void;
    updateProduct: (id: number, product: Partial<Product>) => void;
    deleteProduct: (id: number) => void;
}

const ProductsContext = createContext<ProductsContextType | undefined>(undefined);

export function ProductsProvider({ children }: { children: React.ReactNode }) {
    const [products, setProducts] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const mapBackendProduct = (p: any): Product => parseProduct(p);

    const refreshProducts = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            if (token) {
                const userProducts = await fetchUserProducts();
                setProducts(userProducts.map(mapBackendProduct));
            } else {
                const demoProducts = await fetchDemoProducts();
                
                // Ensure all demo products have unique IDs to prevent React key conflicts
                // Check if any products have null or duplicate IDs
                const productIds = demoProducts.map(p => p.product_id);
                const hasNullOrDuplicate = productIds.some((id, index) => 
                    id === null || id === undefined || productIds.indexOf(id) !== index
                );
                
                if (hasNullOrDuplicate) {
                    // Assign sequential negative IDs to ensure uniqueness
                    const fixedProducts = demoProducts.map((product, index) => ({
                        ...product,
                        product_id: -(index + 1)  // -1, -2, -3, etc.
                    }));
                    setProducts(fixedProducts.map(mapBackendProduct));
                } else {
                    setProducts(demoProducts.map(mapBackendProduct));
                }
            }
        } catch (error) {
            console.error('Failed to fetch products:', error);
            // On error, set empty array - backend demo products are the single source of truth
            setProducts([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshProducts();
    }, [refreshProducts]);

    // Listen for auth changes to refresh products
    // Listen for products added/updated from the labeling flow (LabelAddProduct.tsx)
    useEffect(() => {
        const handleProductLibraryChange = () => refreshProducts();
        window.addEventListener('productLibraryChange', handleProductLibraryChange);
        return () => window.removeEventListener('productLibraryChange', handleProductLibraryChange);
    }, [refreshProducts]);

    useEffect(() => {
        const handleAuthChange = (event: Event) => {
            const customEvent = event as CustomEvent;
            // If user logged out, immediately clear products to avoid showing stale data
            if (customEvent.detail?.isAuthenticated === false || customEvent.detail?.action === 'logout') {
                setProducts([]);
            }
            // Then refresh products (will load demo products if logged out, user products if logged in)
            refreshProducts();
        };
        window.addEventListener('authStateChange', handleAuthChange);
        return () => window.removeEventListener('authStateChange', handleAuthChange);
    }, [refreshProducts]);

    const addProduct = (product: Product) => {
        setProducts([...products, product]);
    };

    const updateProduct = (id: number, updatedProduct: Partial<Product>) => {
        setProducts(products.map((p) => (p.id === id ? { ...p, ...updatedProduct } : p)));
    };

    const deleteProduct = (id: number) => {
        setProducts(products.filter((p) => p.id !== id));
    };

    return (
        <ProductsContext.Provider value={{ products, isLoading, refreshProducts, addProduct, updateProduct, deleteProduct }}>
            {children}
        </ProductsContext.Provider>
    );
}

export function useProducts() {
    const context = useContext(ProductsContext);
    if (context === undefined) {
        // Return empty data if used outside provider (shouldn't happen in normal flow)
        return {
            products: [],
            isLoading: false,
            refreshProducts: async () => { },
            addProduct: () => { },
            updateProduct: () => { },
            deleteProduct: () => { },
        };
    }
    return context;
}
